import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Pencil,
  Check,
  X,
  Download,
  Mail,
  SendHorizonal,
  MapPin,
  Plane,
  Ship,
  Truck,
  TrainFront,
  Route,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/auth'
import {
  applyLatestAssignmentEvent,
  type Case,
  type CaseEvent,
  type EntryField,
  type EntryLine,
  type IntakeDocument,
  type Transmission,
  formatDate,
  formatDateTime,
  formatUSD,
} from '@/lib/cases'
import { VICE_POC_ASSIGNEES, assigneeNameForEmail } from '@/lib/assignees'
import { formatPortDisplay } from '@/lib/ports'
import {
  lookupCountryCoordinate,
  lookupDestinationCoordinate,
} from '@/lib/shipmentPath'
import { StatusBadge } from '@/components/app/StatusBadge'
import { Tabs } from '@/components/app/Tabs'
import {
  DocumentsWorkspaceTab,
  ReviewWorkspaceTab,
} from '@/features/case-workspace/CaseWorkspaceTabs'
import { countCorrectiveActionItems } from '@/features/case-workspace/ReviewWorkspaceTab'
import { applyCbp7501DocumentAutomation } from '@/features/case-workspace/cbp7501LineAutomation'
import { presentCaseEvent, type ActivityTone } from '@/features/case-workspace/activity'
import {
  buildTransmitDocuments,
  downloadTransmitDocument,
  requestEmailTransmitPlaceholder,
} from '@/features/case-workspace/transmit'
import {
  buildInitialReviewState,
} from '@/features/case-workspace/mockData'
import { caseWorkspaceService } from '@/features/case-workspace/service'
import type {
  CaseReviewState,
  DocumentWorkspaceFocusTarget,
  SourceDocumentRecord,
  StructuredDocumentRecord,
} from '@/features/case-workspace/types'
import {
  evaluateReviewReadiness,
  validateStructuredDocument,
} from '@/features/case-workspace/utils'
import { DOCUMENT_DEFINITIONS } from '@/features/case-workspace/schemas'
import { TrackerWorkspacePanel } from '@/features/case-workspace/WorkspaceShared'
import {
  CBP_7501_COUNTRY_CODE_OPTIONS,
  CBP_7501_MODE_OF_TRANSPORT_OPTIONS,
} from '@/features/case-workspace/cbp7501'
import {
  MVP_VISIBLE_DOCUMENT_TYPES,
  filterVisibleReviewState,
  filterVisibleSourceDocuments,
  filterVisibleStructuredDocuments,
  isMvpVisibleDocumentType,
} from '@/features/case-workspace/mvpVisibility'

function sectionIdForReviewField(documentType: StructuredDocumentRecord['documentType'], fieldKey: string) {
  return DOCUMENT_DEFINITIONS[documentType].sections.find(
    (section) => section.kind !== 'line_items' && section.fields.some((field) => field.key === fieldKey),
  )?.id
}

function applyReviewRecordAutomation(record: StructuredDocumentRecord, actorLabel: string) {
  if (record.documentType === 'cbp_7501') {
    return applyCbp7501DocumentAutomation(record, actorLabel)
  }
  return record
}

function fieldText(record: StructuredDocumentRecord | undefined, key: string) {
  return record?.fields[key]?.displayValue || record?.fields[key]?.value || ''
}

function transportLabelForCode(code: string) {
  const match = CBP_7501_MODE_OF_TRANSPORT_OPTIONS.find((option) => option.value === code)
  return match?.label ?? code
}

function countryNameForCode(code: string) {
  const normalizedCode = code.trim().toUpperCase()
  const match = CBP_7501_COUNTRY_CODE_OPTIONS.flatMap((group) => group.options).find(
    (option) => option.value === normalizedCode,
  )
  const name = match?.label.replace(/^[A-Z]{2}\s+—\s+/, '').trim()
  return name ? `${name} (${normalizedCode})` : code
}

function TransportModeIcon({ code }: { code: string }) {
  const Icon =
    code.startsWith('10') || code.startsWith('11') || code.startsWith('12')
      ? Ship
      : code.startsWith('20') || code.startsWith('21')
        ? TrainFront
        : code.startsWith('30') || code.startsWith('31') || code.startsWith('32') || code.startsWith('33') || code.startsWith('34') || code === '60'
          ? Truck
          : code.startsWith('40') || code.startsWith('41')
            ? Plane
            : Route

  return <Icon size={22} aria-hidden="true" />
}

function ShipmentPathMap({
  originLabel,
  destinationLabel,
  transportLabel,
  transportCode,
  pending,
}: {
  originLabel: string
  destinationLabel: string
  transportLabel: string
  transportCode: string
  pending: boolean
}) {
  return (
    <div className="mt-4 overflow-hidden rounded-2xl border border-[#12335a] bg-[radial-gradient(circle_at_top_left,_rgba(37,86,145,0.35),_transparent_36%),linear-gradient(180deg,_#071a33,_#0b2547_48%,_#08172c)] shadow-[0_24px_60px_rgba(4,16,35,0.22)]">
      <div className="relative">
        {pending && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-[#08172c]/76 backdrop-blur-[2px]">
            <div className="rounded-full border border-[#355d8b] bg-[#0d2340] px-4 py-2 text-sm font-semibold text-white shadow-sm">
              Pending CBP Form 7501 Details
            </div>
          </div>
        )}
        <div className="flex items-start justify-between gap-4 border-b border-white/10 px-6 py-5">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#89a7cb]">
              Shipment tracker
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-[1.7rem] font-bold uppercase tracking-[0.08em] text-white">
              <span>{originLabel || 'Pending origin'}</span>
              <span className="text-[#d8ff2b]">→</span>
              <span>{destinationLabel || 'Pending port of unlading'}</span>
            </div>
          </div>
        </div>
        <div className="grid gap-3 border-t border-white/10 px-6 py-5 md:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-left">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#5f7595] text-[#9fb4cf]">
                <MapPin size={17} aria-hidden="true" />
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#89a7cb]">Country of origin</div>
                <div className="mt-1 text-sm font-semibold text-white">{originLabel || 'Awaiting origin details'}</div>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-left">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#5f7595] text-[#9fb4cf]">
                <TransportModeIcon code={transportCode} />
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#89a7cb]">Mode of transport</div>
                <div className="mt-1 text-sm font-semibold text-white">{transportLabel || 'Awaiting transport mode'}</div>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-left">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#5f7595] text-[#9fb4cf]">
                <MapPin size={17} aria-hidden="true" />
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#89a7cb]">Port of unlading</div>
                <div className="mt-1 text-sm font-semibold text-white">{destinationLabel || 'Awaiting port details'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function resetSectionCompletionForReviewEdit(
  record: StructuredDocumentRecord,
  sectionIds: Array<string | undefined>,
  actorLabel: string,
  detail: string,
) {
  const validSectionIds = sectionIds.filter((sectionId): sectionId is string => Boolean(sectionId))
  if (validSectionIds.length === 0) return record
  const sectionIdsToReset = validSectionIds.filter((sectionId) => record.sectionStates[sectionId]?.status === 'complete')
  const shouldResetDocument = record.status === 'complete'

  if (sectionIdsToReset.length === 0 && !shouldResetDocument) return record

  return {
    ...record,
    status: shouldResetDocument ? 'in_progress' : record.status,
    sectionStates:
      sectionIdsToReset.length > 0
        ? {
            ...record.sectionStates,
            ...Object.fromEntries(
              sectionIdsToReset.map((sectionId) => [
                sectionId,
                {
                  ...record.sectionStates[sectionId],
                  sectionId,
                  status: 'in_progress' as const,
                  lastUpdatedAt: new Date().toISOString(),
                  lastUpdatedBy: actorLabel,
                },
              ]),
            ),
          }
        : record.sectionStates,
    auditHistory: [
      {
        id: `${record.id}-${sectionIdsToReset.join('-') || 'document'}-section-reopened-${Date.now()}`,
        at: new Date().toISOString(),
        actor: actorLabel,
        action: 'section_reopened_for_editing',
        detail,
      },
      ...record.auditHistory,
    ],
    lastUpdatedAt: new Date().toISOString(),
    lastUpdatedBy: actorLabel,
  }
}

export function CaseDetail() {
  const { id = '' } = useParams<{ id: string }>()
  const { user } = useAuth()
  const [kase, setKase] = useState<Case | null>(null)
  const [docs, setDocs] = useState<IntakeDocument[]>([])
  const [sourceDocuments, setSourceDocuments] = useState<SourceDocumentRecord[]>([])
  const [fields, setFields] = useState<EntryField[]>([])
  const [lines, setLines] = useState<EntryLine[]>([])
  const [events, setEvents] = useState<CaseEvent[]>([])
  const [transmissions, setTransmissions] = useState<Transmission[]>([])
  const [structuredDocuments, setStructuredDocuments] = useState<StructuredDocumentRecord[]>([])
  const [reviewState, setReviewState] = useState<CaseReviewState | null>(null)
  const [activeTab, setActiveTab] = useState('overview')
  const [documentFocusTarget, setDocumentFocusTarget] = useState<DocumentWorkspaceFocusTarget | null>(null)
  const [loading, setLoading] = useState(true)
  const [editingTitle, setEditingTitle] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')
  const [titleBusy, setTitleBusy] = useState(false)
  const [assignmentBusy, setAssignmentBusy] = useState(false)
  const actorLabel = user?.email ?? 'Current user'

  const visibleStructuredDocuments = useMemo(
    () => filterVisibleStructuredDocuments(structuredDocuments),
    [structuredDocuments],
  )
  const visibleSourceDocuments = useMemo(
    () => filterVisibleSourceDocuments(sourceDocuments, visibleStructuredDocuments),
    [sourceDocuments, visibleStructuredDocuments],
  )
  const visibleReviewState = useMemo(
    () => (reviewState ? filterVisibleReviewState(reviewState, visibleStructuredDocuments) : null),
    [reviewState, visibleStructuredDocuments],
  )
  const header7501Record = useMemo(
    () =>
      visibleStructuredDocuments.find(
        (record) => record.documentType === 'cbp_7501' && record.status !== 'missing',
      ),
    [visibleStructuredDocuments],
  )
  const headerMeta = useMemo(() => {
    const parseCurrencyValue = (value?: string) => {
      if (!value) return null
      const normalized = value.replace(/[^0-9.-]/g, '')
      const numeric = Number(normalized)
      return Number.isFinite(numeric) ? numeric : null
    }

    const port = formatPortDisplay(
      header7501Record?.fields.port_of_entry?.displayValue
      || header7501Record?.fields.port_of_entry?.value
      || kase?.port_of_entry,
    ) || '—'
    const totalValueAmount = parseCurrencyValue(
      header7501Record?.fields.total_entered_value?.displayValue
      || header7501Record?.fields.total_entered_value?.value,
    )
    const estimatedDutyAmount = parseCurrencyValue(
      header7501Record?.fields.duty_total?.displayValue
      || header7501Record?.fields.duty_total?.value,
    )
    const created =
      header7501Record?.fields.summary_date?.displayValue
      || header7501Record?.fields.summary_date?.value
      || header7501Record?.fields.entry_date?.displayValue
      || header7501Record?.fields.entry_date?.value
      || formatDate(kase?.created_at ?? '')

    return {
      port,
      totalValue: totalValueAmount != null ? formatUSD(totalValueAmount) : formatUSD(kase?.total_value_usd ?? 0),
      estimatedDuty: estimatedDutyAmount != null ? formatUSD(estimatedDutyAmount) : formatUSD(kase?.total_duty_usd ?? 0),
      created,
    }
  }, [header7501Record, kase])

  // Use a ref so applyWorkspaceState doesn't depend on sourceDocuments
  // state — avoids a refresh → setSourceDocuments → applyWorkspaceState
  // recreation → refresh loop.
  const sourceDocumentsRef = useRef(sourceDocuments)
  useEffect(() => {
    sourceDocumentsRef.current = sourceDocuments
  }, [sourceDocuments])

  const applyWorkspaceState = useCallback(
    (
      records: StructuredDocumentRecord[],
      nextReviewState?: CaseReviewState | null,
      nextSourceDocuments?: SourceDocumentRecord[],
    ) => {
      const effectiveSourceDocuments = nextSourceDocuments ?? sourceDocumentsRef.current
      const normalizedRecords = records.map((record) => {
        const validationIssues = validateStructuredDocument(record)
        const hasBlockingIssues = validationIssues.some((issue) => issue.severity === 'error')

        return {
          ...record,
          validationIssues,
          status:
            hasBlockingIssues && record.status === 'missing'
              ? 'missing'
              : record.status,
        }
      })

      setStructuredDocuments(normalizedRecords)

      const visibleRecords = filterVisibleStructuredDocuments(normalizedRecords)
      const visibleSources = filterVisibleSourceDocuments(effectiveSourceDocuments, visibleRecords)
      const readiness = evaluateReviewReadiness(visibleRecords, visibleSources)
      setReviewState((current) => {
        const baseReviewState =
          nextReviewState ??
          current ??
          (id ? buildInitialReviewState(id) : null)

        if (!baseReviewState) return null

        const findings = baseReviewState.findings
          .filter((finding) => {
            if (finding.documentType === 'case') return true
            if (finding.documentType && isMvpVisibleDocumentType(finding.documentType)) return true
            if (finding.documentId) {
              return visibleRecords.some((record) => record.id === finding.documentId)
            }
            return false
          })
          .map((finding) => {
          if (!finding.fieldKey) return finding

          // Resolve by ID first; fall back to documentType so seeded
          // findings with stale/placeholder IDs still link correctly.
          const linkedRecord =
            normalizedRecords.find((record) => record.id === finding.documentId) ??
            (finding.documentType
              ? normalizedRecords.find((record) => record.documentType === finding.documentType)
              : undefined)

          // Patch the finding's documentId to the real record UUID so
          // downstream "open document" targets resolve on click.
          const resolvedFinding = linkedRecord && linkedRecord.id !== finding.documentId
            ? { ...finding, documentId: linkedRecord.id }
            : finding
          const linkedRecommendation = linkedRecord?.awaRecommendations.find(
            (recommendation) => recommendation.findingId === resolvedFinding.id && recommendation.fieldKey === resolvedFinding.fieldKey,
          )

          if (!linkedRecommendation) return resolvedFinding

          return {
            ...resolvedFinding,
            status:
              linkedRecommendation.state === 'resolved'
                ? 'resolved'
                : linkedRecommendation.state === 'partially_applied'
                  ? 'investigating'
              : resolvedFinding.status,
          }
        })

        const riskSummary = {
          total: findings.length,
          open: findings.filter((finding) => finding.status !== 'resolved').length,
          high: findings.filter((finding) => (finding.severity === 'high' || finding.severity === 'blocks_acceptance') && finding.status !== 'resolved').length,
        }

        const readinessStatus =
          baseReviewState.awaStatus === 'completed'
            ? 'review_completed'
            : baseReviewState.awaStatus === 'in_progress'
              ? 'review_in_progress'
              : readiness.readinessStatus

        const readinessSummary =
          baseReviewState.awaStatus === 'completed'
            ? 'AWA review is complete. Continue resolving any open findings and finalize the case record.'
            : baseReviewState.awaStatus === 'in_progress'
              ? 'AWA is actively reviewing this case and surfacing items that may need attention.'
              : readiness.readinessSummary

        return {
          ...baseReviewState,
          findings,
          riskSummary,
          ...readiness,
          readinessStatus,
          readinessSummary,
        }
      })
    },
    [id],
  )

  async function saveCaseTitle() {
    if (!kase || titleBusy) return
    const nextTitle = draftTitle.trim()
    if (!nextTitle || nextTitle === kase.title) {
      setDraftTitle(kase.title)
      setEditingTitle(false)
      return
    }

    setTitleBusy(true)
    const previousTitle = kase.title
    const { data, error } = await supabase
      .from('cases')
      .update({ title: nextTitle })
      .eq('id', kase.id)
      .select('*')
      .single()

    if (!error && data) {
      setKase(data as Case)
      setEditingTitle(false)
      if (user?.id) {
        const { data: eventData } = await supabase.from('case_events').insert({
          case_id: kase.id,
          actor_id: user.id,
          kind: 'case.updated',
          payload: {
            actor_label: actorLabel,
            field: 'title',
            previous_value: previousTitle,
            next_value: nextTitle,
          },
        }).select('*').single()
        if (eventData) {
          setEvents((current) => [eventData as CaseEvent, ...current])
        }
      }
    } else {
      setDraftTitle(previousTitle)
      setEditingTitle(false)
    }
    setTitleBusy(false)
  }

  const applyReviewQuickFix = useCallback(
    async (target: { documentId: string; recommendationId: string }) => {
      const record = structuredDocuments.find((item) => item.id === target.documentId)
      const recommendation = record?.awaRecommendations.find((item) => item.id === target.recommendationId)
      if (!record || !recommendation?.recommendedValue) return

      const sectionId = sectionIdForReviewField(record.documentType, recommendation.fieldKey)
      const nextRecord = applyReviewRecordAutomation(
        resetSectionCompletionForReviewEdit(
          {
            ...record,
            fields: {
              ...record.fields,
              [recommendation.fieldKey]: {
                ...record.fields[recommendation.fieldKey],
                value: recommendation.recommendedValue,
                displayValue: recommendation.recommendedValue,
                origin: 'reviewed',
                reviewedValue: recommendation.recommendedValue,
                lastUpdatedAt: new Date().toISOString(),
                lastUpdatedBy: actorLabel,
              },
            },
            awaRecommendations: record.awaRecommendations.map((item) =>
              item.id === recommendation.id
                ? { ...item, currentValue: recommendation.recommendedValue ?? item.currentValue, state: 'resolved' }
                : item,
            ),
            auditHistory: [
              {
                id: `${record.id}-${recommendation.id}-applied`,
                at: new Date().toISOString(),
                actor: actorLabel,
                action: 'recommendation_applied',
                detail: `AWA recommendation applied to ${recommendation.fieldKey} from the Review tab.`,
              },
              ...record.auditHistory,
            ],
            lastUpdatedAt: new Date().toISOString(),
            lastUpdatedBy: actorLabel,
          },
          [sectionId],
          actorLabel,
          `${recommendation.fieldKey} was updated from the Review tab after the section had been marked complete.`,
        ),
        actorLabel,
      )

      const { record: savedRecord, event } = await caseWorkspaceService.saveStructuredDocument({
        record: nextRecord,
        actorId: user?.id,
        actorLabel,
      })

      setEvents((current) => [event, ...current])
      applyWorkspaceState(
        structuredDocuments.map((item) => (item.id === savedRecord.id ? savedRecord : item)),
        undefined,
        sourceDocuments,
      )
    },
    [actorLabel, applyWorkspaceState, sourceDocuments, structuredDocuments, user?.id],
  )

  const refresh = useCallback(async () => {
    if (!id) return
    setLoading(true)
    const [c, d, f, l, e, t] = await Promise.all([
      supabase.from('cases').select('*').eq('id', id).maybeSingle(),
      supabase.from('intake_documents').select('*').eq('case_id', id).order('uploaded_at', { ascending: false }),
      supabase.from('entry_summary_fields').select('*').eq('case_id', id).order('updated_at', { ascending: false }),
      supabase.from('entry_summary_lines').select('*').eq('case_id', id).order('line_no'),
      supabase.from('case_events').select('*').eq('case_id', id).order('occurred_at', { ascending: false }).limit(50),
      supabase.from('transmissions').select('*').eq('case_id', id).order('created_at', { ascending: false }),
    ])
    const caseData = (c.data as Case | null) ?? null
    const docsData = (d.data as IntakeDocument[]) ?? []
    const fieldsData = (f.data as EntryField[]) ?? []
    const linesData = (l.data as EntryLine[]) ?? []
    const eventsData = (e.data as CaseEvent[]) ?? []
    const transmissionsData = (t.data as Transmission[]) ?? []
    const effectiveCaseData = applyLatestAssignmentEvent(caseData, eventsData)

    setKase(effectiveCaseData)
    setDocs(docsData)
    setFields(fieldsData)
    setLines(linesData)
    setEvents(eventsData)
    setTransmissions(transmissionsData)

    if (effectiveCaseData) {
      const workspaceState = await caseWorkspaceService.ensureStructuredDocuments({
        kase: effectiveCaseData,
        actorId: user?.id,
        actorLabel,
        intakeDocuments: docsData,
        fields: fieldsData,
        lines: linesData,
      })
      setSourceDocuments(workspaceState.sourceDocuments)
      // Pass undefined for reviewState so applyWorkspaceState preserves
      // the current in-memory review context (awaStatus, chat messages,
      // finding progression) across refreshes. The initial seed is only
      // used when current is null (first load).
      applyWorkspaceState(workspaceState.structuredDocuments, undefined, workspaceState.sourceDocuments)
    } else {
      setSourceDocuments([])
      setStructuredDocuments([])
      setReviewState(null)
    }

    setLoading(false)
  }, [actorLabel, applyWorkspaceState, id, user?.id])

  useEffect(() => {
    // refresh() updates state after awaits, which is the recommended
    // pattern; the lint rule can't trace through the async boundary.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh()
  }, [refresh])

  // Poll while OCR is processing.
  useEffect(() => {
    const anyPending = docs.some((d) => d.ocr_status === 'pending' || d.ocr_status === 'processing')
    if (!anyPending || activeTab === 'documents') return
    const handle = window.setInterval(() => {
      void refresh()
    }, 3000)
    return () => window.clearInterval(handle)
  }, [activeTab, docs, refresh])

  if (loading && !kase) {
    return <div className="text-sm text-neutral-500">Loading case…</div>
  }
  if (!kase) {
    return (
      <div>
        <p className="text-sm text-neutral-700">Case not found.</p>
        <Link to="/cases" className="text-sm font-semibold text-secondary hover:text-primary">
          ← Back to cases
        </Link>
      </div>
    )
  }

  const flagged = fields.filter((f) => f.flagged).length
  const overviewLineItemCount = header7501Record?.lineItems?.length ?? lines.length
  const overviewLastUpdated = header7501Record?.lastUpdatedAt ?? kase.updated_at
  const openCorrectiveActionsCount = visibleReviewState
    ? countCorrectiveActionItems(visibleReviewState.findings, visibleStructuredDocuments)
    : 0
  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      content: (
        <OverviewTab
          kase={kase}
          record={header7501Record}
          linesCount={overviewLineItemCount}
          lastUpdatedAt={overviewLastUpdated}
          flagged={flagged}
          assignmentBusy={assignmentBusy}
          onAssignCase={async (nextEmail) => {
            if (assignmentBusy) return
            setAssignmentBusy(true)
            const nextName = assigneeNameForEmail(nextEmail)
            const previousAssignee = kase.assigned_agent_name || kase.assigned_agent_email || 'Unassigned'
            const nextStatus = nextEmail ? 'intake' : 'draft'
            const optimisticCase = {
              ...kase,
              assigned_agent_email: nextEmail,
              assigned_agent_name: nextName,
              status: nextStatus,
              updated_at: new Date().toISOString(),
            } satisfies Case
            setKase(optimisticCase)

            const { data, error } = await supabase
              .from('cases')
              .update({
                assigned_agent_email: nextEmail,
                assigned_agent_name: nextName,
                status: nextStatus,
              })
              .eq('id', kase.id)
              .select('*')
              .single()

            let nextCaseState = optimisticCase

            if (!error && data) {
              nextCaseState = {
                ...(data as Case),
                assigned_agent_email: nextEmail,
                assigned_agent_name: nextName,
              }
            } else {
              const { data: fallbackData, error: fallbackError } = await supabase
                .from('cases')
                .update({ status: nextStatus })
                .eq('id', kase.id)
                .select('*')
                .single()

              if (!fallbackError && fallbackData) {
                nextCaseState = {
                  ...(fallbackData as Case),
                  assigned_agent_email: nextEmail,
                  assigned_agent_name: nextName,
                }
              }
            }

            if (user?.id) {
              const { data: eventData } = await supabase.from('case_events').insert({
                case_id: kase.id,
                actor_id: user.id,
                kind: 'case.assignment_updated',
                payload: {
                  actor_label: actorLabel,
                  previous_value: previousAssignee,
                  next_value: nextName || 'Unassigned',
                  next_email: nextEmail,
                  next_status: nextStatus,
                },
              }).select('*').single()

              if (eventData) {
                setEvents((current) => [eventData as CaseEvent, ...current])
                nextCaseState = applyLatestAssignmentEvent(nextCaseState, [eventData as CaseEvent]) ?? nextCaseState
              }
            }

            setKase(nextCaseState)
            setAssignmentBusy(false)
          }}
        />
      ),
    },
    {
      id: 'documents',
      label: 'Documents',
      badge: visibleStructuredDocuments.length || undefined,
      content: (
        <DocumentsWorkspaceTab
          caseId={id}
          actorId={user?.id}
          actorLabel={actorLabel}
          sourceDocuments={visibleSourceDocuments}
          structuredDocuments={visibleStructuredDocuments}
          visibleDocumentTypes={MVP_VISIBLE_DOCUMENT_TYPES}
          showUploadArea={false}
          focusTarget={documentFocusTarget}
          onUploaded={refresh}
          onSourceDocumentsChange={(nextSourceDocuments, nextStructuredDocuments, removedIds = []) => {
            setSourceDocuments(nextSourceDocuments)
            if (removedIds.length > 0) {
              setDocs((current) => current.filter((doc) => !removedIds.includes(doc.id)))
            }
            applyWorkspaceState(nextStructuredDocuments ?? structuredDocuments, undefined, nextSourceDocuments)
          }}
          onStructuredDocumentsChange={(records) => applyWorkspaceState(records, undefined, sourceDocuments)}
          onActivityEvent={(event) => setEvents((current) => [event, ...current])}
          onFocusHandled={() => setDocumentFocusTarget(null)}
        />
      ),
    },
    {
      id: 'review',
      label: 'Review',
      badge: openCorrectiveActionsCount || undefined,
      content: visibleReviewState ? (
        <ReviewWorkspaceTab
          actorId={user?.id}
          actorLabel={actorLabel}
          structuredDocuments={visibleStructuredDocuments}
          reviewState={visibleReviewState}
          onReviewStateChange={setReviewState}
          onActivityEvent={(event) => setEvents((current) => [event, ...current])}
          onOpenFindingTarget={(target) => {
            setDocumentFocusTarget(target)
            setActiveTab('documents')
          }}
          onApplyQuickFix={applyReviewQuickFix}
        />
      ) : (
        <div className="text-sm text-neutral-500">Preparing review workspace…</div>
      ),
    },
    {
      id: 'transmit',
      label: 'Transmit',
      content: (
        <TransmitTab
          kase={kase}
          sourceDocuments={visibleSourceDocuments}
          structuredDocuments={visibleStructuredDocuments}
          transmissions={transmissions}
          userId={user?.id}
          actorLabel={actorLabel}
          onActivityEvent={(event) => setEvents((current) => [event, ...current])}
          onChange={refresh}
        />
      ),
    },
    {
      id: 'activity',
      label: 'Activity',
      badge: events.length || undefined,
      content: <ActivityTab events={events} />,
    },
  ]

  return (
    <section className="space-y-4">
      <Link
        to="/cases"
        className="inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:text-primary"
      >
        <ArrowLeft size={14} aria-hidden="true" />
        Back to cases
      </Link>

      <header className="sticky top-3 z-10 rounded-lg border border-neutral-200 bg-white/95 p-4 shadow-sm backdrop-blur">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                <span className="font-mono">{kase.case_number}</span>
              </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {editingTitle ? (
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <input
                    type="text"
                    value={draftTitle}
                    onChange={(event) => setDraftTitle(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                        void saveCaseTitle()
                      }
                      if (event.key === 'Escape') {
                        setDraftTitle(kase.title)
                        setEditingTitle(false)
                      }
                    }}
                    className="min-w-0 flex-1 rounded border border-neutral-300 px-3 py-1.5 text-lg font-bold text-primary"
                    aria-label="Case title"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => void saveCaseTitle()}
                    disabled={titleBusy}
                    className="inline-flex items-center justify-center rounded border border-neutral-300 p-1.5 text-secondary hover:bg-secondary-lightest disabled:opacity-50"
                    aria-label="Save case title"
                  >
                    <Check size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDraftTitle(kase.title)
                      setEditingTitle(false)
                    }}
                    disabled={titleBusy}
                    className="inline-flex items-center justify-center rounded border border-neutral-300 p-1.5 text-neutral-600 hover:bg-neutral-100 disabled:opacity-50"
                    aria-label="Cancel case title edit"
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <div className="flex min-w-0 items-center gap-2">
                  <h1 className="min-w-0 truncate text-lg font-bold text-primary">{kase.title}</h1>
                  <button
                    type="button"
                    onClick={() => {
                      setDraftTitle(kase.title)
                      setEditingTitle(true)
                    }}
                    className="inline-flex items-center justify-center rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-secondary"
                    aria-label="Edit case title"
                  >
                    <Pencil size={14} aria-hidden="true" />
                  </button>
                </div>
              )}
              <StatusBadge status={kase.status} />
              <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-0.5 text-xs font-semibold text-secondary">
                {kase.assigned_agent_name || assigneeNameForEmail(kase.assigned_agent_email) || 'Unassigned'}
              </span>
              {kase.flagged_count > 0 && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-coral">
                  <AlertTriangle size={12} aria-hidden="true" />
                  {kase.flagged_count} flag{kase.flagged_count === 1 ? '' : 's'}
                </span>
              )}
            </div>
            {kase.summary && (
              <p className="mt-1 line-clamp-1 text-sm text-neutral-600 whitespace-pre-wrap">{kase.summary}</p>
            )}
          </div>
          <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <CompactMeta label="Port" value={headerMeta.port} />
            <CompactMeta label="Total value" value={headerMeta.totalValue} strong />
            <CompactMeta label="Estimated duty" value={headerMeta.estimatedDuty} strong />
            <CompactMeta label="Created" value={headerMeta.created} />
          </dl>
        </div>
      </header>

      <Tabs tabs={tabs} activeId={activeTab} onChange={setActiveTab} />
    </section>
  )
}

/* ─── tab components ────────────────────────────────────────────────── */

function OverviewTab({
  kase,
  record,
  linesCount,
  lastUpdatedAt,
  flagged,
  assignmentBusy,
  onAssignCase,
}: {
  kase: Case
  record?: StructuredDocumentRecord
  linesCount: number
  lastUpdatedAt: string
  flagged: number
  assignmentBusy: boolean
  onAssignCase: (nextEmail: string | null) => Promise<void> | void
}) {
  const portOfEntry = formatPortDisplay(fieldText(record, 'port_of_entry') || kase.port_of_entry || '')
  const usPortOfUnlading = formatPortDisplay(fieldText(record, 'us_port_of_unlading'))
  const destinationLabel = usPortOfUnlading || portOfEntry || ''
  const exportingCountryCode = fieldText(record, 'country_of_origin') || fieldText(record, 'exporting_country')
  const originCountry = countryNameForCode(exportingCountryCode)
  const transportCode = fieldText(record, 'mode_of_transport')
  const transportLabel = transportLabelForCode(transportCode)
  const originCoordinate = lookupCountryCoordinate(exportingCountryCode)
  const destinationLocation = lookupDestinationCoordinate(destinationLabel)
  const destinationCoordinate = destinationLocation?.coordinate ?? null
  const hasShipmentPathDetails = Boolean(originCountry && destinationLabel && transportCode && originCoordinate && destinationCoordinate)

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="md:col-span-2">
        <ShipmentPathMap
          originLabel={originCountry}
          destinationLabel={destinationLabel}
          transportLabel={transportLabel}
          transportCode={transportCode}
          pending={!hasShipmentPathDetails}
        />
      </div>
      <Card title="Line items" value={linesCount} hint={`${flagged} flag${flagged === 1 ? '' : 's'}`} />
      <Card title="Last update" value={formatDateTime(lastUpdatedAt)} />
      <div className="md:col-span-2 bg-white border border-neutral-200 rounded-lg p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
              Case assignment
            </h3>
            <p className="mt-2 text-sm text-neutral-700">
              Select an agent to pick up this case. Cases with no assignee are treated as new work in the MVP queue.
            </p>
          </div>
          <div className="w-full md:max-w-sm">
            <label htmlFor="case-assignment" className="block text-xs font-semibold uppercase tracking-wide text-neutral-700 mb-1">
              Assigned agent
            </label>
            <select
              id="case-assignment"
              value={kase.assigned_agent_email ?? ''}
              disabled={assignmentBusy}
              onChange={(event) => void onAssignCase(event.target.value || null)}
              className="w-full rounded border border-neutral-300 px-3 py-2 text-sm focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 disabled:bg-neutral-100 disabled:text-neutral-500"
            >
              <option value="">Unassigned / New</option>
              {VICE_POC_ASSIGNEES.map((assignee) => (
                <option key={assignee.email} value={assignee.email}>
                  {assignee.name} ({assignee.email})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="md:col-span-2 bg-white border border-neutral-200 rounded-lg p-5 shadow-sm">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
          AI Workflow Automation
        </h3>
        <p className="mt-2 text-sm text-neutral-700">
          AWA processes each uploaded document automatically — OCR, field bucketing, validation,
          and recommendations — and writes results into this case. Real classification + chat
          assistant ship in a follow-up release; today the pipeline is stubbed for demo.
        </p>
      </div>
    </div>
  )
}

function Card({ title, value, hint }: { title: string; value: string | number; hint?: string }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-5 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{title}</div>
      <div className="mt-2 text-2xl font-bold text-primary">{value}</div>
      {hint && <div className="mt-1 text-xs text-neutral-500">{hint}</div>}
    </div>
  )
}

function CompactMeta({
  label,
  value,
  strong = false,
}: {
  label: string
  value: string
  strong?: boolean
}) {
  return (
    <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">{label}</dt>
      <dd className={`mt-1 truncate text-base font-bold text-primary ${strong ? 'text-primary' : 'text-primary/95'}`}>{value}</dd>
    </div>
  )
}

function TransmitTab({
  kase,
  sourceDocuments,
  structuredDocuments,
  transmissions,
  userId,
  actorLabel,
  onActivityEvent,
  onChange,
}: {
  kase: Case
  sourceDocuments: SourceDocumentRecord[]
  structuredDocuments: StructuredDocumentRecord[]
  transmissions: Transmission[]
  userId: string | undefined
  actorLabel: string
  onActivityEvent: (event: CaseEvent) => void
  onChange: () => void | Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [emailBusy, setEmailBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const transmitDocuments = useMemo(
    () => buildTransmitDocuments({ kase, sourceDocuments, structuredDocuments }),
    [kase, sourceDocuments, structuredDocuments],
  )
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('')
  const selectedDocument = useMemo(() => {
    if (transmitDocuments.length === 0) return undefined
    const selected = transmitDocuments.find((document) => document.id === selectedDocumentId)
    if (selected) return selected
    return transmitDocuments.find((document) => document.readinessStatus === 'ready') ?? transmitDocuments[0]
  }, [selectedDocumentId, transmitDocuments])

  async function transmit() {
    setError(null)
    setStatusMessage(null)
    setBusy(true)
    try {
      const { error: invErr } = await supabase.functions.invoke('transmit-to-ace', {
        body: { case_id: kase.id },
      })
      if (invErr) throw invErr
      setStatusMessage(
        selectedDocument
          ? `${selectedDocument.title} is staged in the transmit workspace while the case is sent through the ACE placeholder.`
          : 'The case was sent through the ACE placeholder.',
      )
      await onChange()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function markReady() {
    if (!userId) return
    setStatusMessage(null)
    await supabase.from('cases').update({ status: 'ready' }).eq('id', kase.id)
    const { data: eventData } = await supabase.from('case_events').insert({
      case_id: kase.id,
      actor_id: userId,
      kind: 'case.marked_ready',
      payload: {
        actor_label: actorLabel,
      },
    }).select('*').single()
    if (eventData) {
      onActivityEvent(eventData as CaseEvent)
    }
    await onChange()
  }

  async function emailTransmit() {
    if (!selectedDocument) return
    setError(null)
    setStatusMessage(null)
    setEmailBusy(true)
    try {
      const result = await requestEmailTransmitPlaceholder(selectedDocument)
      setStatusMessage(result.message)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setEmailBusy(false)
    }
  }

  function downloadPdf() {
    if (!selectedDocument) return
    setError(null)
    const result = downloadTransmitDocument(selectedDocument)
    if (result.ok) {
      setStatusMessage(result.message)
      return
    }
    setError(result.message)
  }

  const lastTxn = transmissions[0]
  const canTransmit = kase.status === 'ready'
  const canMarkReady = kase.status === 'review'
  const alreadyTransmitted = kase.status === 'transmitted'
  const transmitSecondaryButtonClassName =
    'inline-flex items-center gap-2 rounded border border-secondary/30 bg-secondary-lightest px-4 py-2 text-sm font-semibold text-primary hover:border-secondary hover:bg-white focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-100 disabled:text-neutral-400'
  const transmitPrimaryButtonClassName =
    'inline-flex items-center gap-2 rounded border border-secondary bg-secondary px-4 py-2 text-sm font-semibold text-white hover:bg-primary hover:border-primary focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-300 disabled:text-neutral-500'

  return (
    <div className="space-y-6">
      <TrackerWorkspacePanel
        eyebrow="Transmit workspace"
        title="Final review and transmission"
        helpText="Review the current PDF version of each case document here, then choose how to send or export it."
        action={
          <div className="flex flex-wrap items-center gap-3">
            {canMarkReady && (
              <button
                type="button"
                onClick={() => void markReady()}
                className={transmitSecondaryButtonClassName}
              >
                Mark ready to transmit
              </button>
            )}
            <button
              type="button"
              onClick={() => void transmit()}
              disabled={busy || !canTransmit}
              className={transmitPrimaryButtonClassName}
            >
              <SendHorizonal size={16} aria-hidden="true" />
              {busy ? 'Transmitting…' : 'Transmit to ACE'}
            </button>
            <button
              type="button"
              onClick={() => void emailTransmit()}
              disabled={emailBusy || selectedDocument?.readinessStatus === 'unavailable'}
              className={transmitSecondaryButtonClassName}
            >
              <Mail size={16} aria-hidden="true" />
              {emailBusy ? 'Preparing email…' : 'Transmit via Email'}
            </button>
            <button
              type="button"
              onClick={downloadPdf}
              disabled={!selectedDocument?.pdfUrl}
              className={transmitSecondaryButtonClassName}
            >
              <Download size={16} aria-hidden="true" />
              Download PDF
            </button>
          </div>
        }
        summaryTiles={[
          {
            label: 'Selected PDF',
            value: selectedDocument?.title ?? 'No document selected',
            icon: <SendHorizonal size={18} aria-hidden="true" />,
          },
          {
            label: 'Readiness',
            value: selectedDocument ? selectedDocument.readinessStatus.replace(/_/g, ' ') : 'Unavailable',
            icon: <CheckCircle2 size={18} aria-hidden="true" />,
          },
          {
            label: 'Last generated',
            value: selectedDocument?.lastGeneratedAt ? formatDateTime(selectedDocument.lastGeneratedAt) : 'Not generated yet',
            icon: <Clock size={18} aria-hidden="true" />,
          },
          {
            label: 'Transmission status',
            value: alreadyTransmitted && lastTxn?.ace_receipt_id ? `Sent · ${lastTxn.ace_receipt_id}` : 'Not transmitted',
            icon: <Mail size={18} aria-hidden="true" />,
          },
        ]}
      />
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {canMarkReady && (
          <span className="text-neutral-600">
            Review cannot be sent to ACE until the case is marked ready.
          </span>
        )}
        {statusMessage && <span className="text-sm text-success">{statusMessage}</span>}
        {error && (
          <span role="alert" className="text-sm text-error">
            {error}
          </span>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[20rem_minmax(0,1fr)]">
        <aside className="bg-white border border-neutral-200 rounded-lg shadow-sm overflow-hidden">
          <div className="border-b border-neutral-200 px-4 py-3">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
              Available PDFs
            </h4>
          </div>
          <ul className="divide-y divide-neutral-200">
            {transmitDocuments.length === 0 ? (
              <li className="p-4 text-sm text-neutral-500">
                No case documents are available for transmission yet.
              </li>
            ) : (
              transmitDocuments.map((document) => (
                <li key={document.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedDocumentId(document.id)}
                    className={`w-full px-4 py-3 text-left ${
                      selectedDocument?.id === document.id
                        ? 'bg-secondary-lightest border-l-4 border-secondary'
                        : 'hover:bg-neutral-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-primary">{document.title}</div>
                        <p className="mt-1 text-xs text-neutral-500">{document.summary}</p>
                      </div>
                      <TransmitStatusPill label={document.readinessStatus.replace(/_/g, ' ')} />
                    </div>
                  </button>
                </li>
              ))
            )}
          </ul>
        </aside>

        <section className="bg-white border border-neutral-200 rounded-lg shadow-sm overflow-hidden">
          <div className="border-b border-neutral-200 px-5 py-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h4 className="text-base font-bold text-primary">
                  {selectedDocument?.title ?? 'PDF preview'}
                </h4>
                <p className="mt-1 text-sm text-neutral-600">
                  Read only review surface for the currently selected transmission document.
                </p>
              </div>
              {selectedDocument && (
                <div className="text-xs text-neutral-500">
                  Source: structured record
                </div>
              )}
            </div>
          </div>
          <div className="h-[42rem] bg-neutral-100">
            {selectedDocument?.pdfUrl ? (
              <iframe
                title={`${selectedDocument.title} PDF preview`}
                src={selectedDocument.pdfUrl}
                className="h-full w-full border-0 bg-white"
              />
            ) : selectedDocument ? (
              <div className="flex h-full items-center justify-center p-8">
                <div className="max-w-md rounded-lg border border-dashed border-neutral-300 bg-white p-6 text-center">
                  <h5 className="text-base font-bold text-primary">PDF not available</h5>
                  <p className="mt-2 text-sm text-neutral-600">
                    {selectedDocument.generationNote}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center p-8">
                <div className="max-w-md rounded-lg border border-dashed border-neutral-300 bg-white p-6 text-center">
                  <h5 className="text-base font-bold text-primary">Select a document</h5>
                  <p className="mt-2 text-sm text-neutral-600">
                    Choose a case document from the list to review its PDF before transmission or download.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      {transmissions.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg shadow-sm">
          <div className="px-5 py-3 border-b border-neutral-200">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
              Transmission history
            </h4>
          </div>
          <ul className="divide-y divide-neutral-200">
            {transmissions.map((t) => (
              <li key={t.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-primary">
                      ACE receipt:{' '}
                      <span className="font-mono">{t.ace_receipt_id ?? '—'}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-neutral-500">
                      {formatDateTime(t.sent_at ?? t.created_at)} &middot; status {t.status}
                      {t.masked_fields.length > 0 && (
                        <> &middot; masked: {t.masked_fields.join(', ')}</>
                      )}
                    </div>
                  </div>
                </div>
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-secondary font-semibold">
                    Payload preview
                  </summary>
                  <pre className="mt-2 max-h-72 overflow-auto bg-neutral-50 border border-neutral-200 rounded p-3 font-mono text-[11px] whitespace-pre-wrap">
                    {JSON.stringify(t.payload_json, null, 2)}
                  </pre>
                </details>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function TransmitStatusPill({ label }: { label: string }) {
  const tone =
    label === 'ready'
      ? 'bg-success/10 text-success'
      : label === 'attention needed'
        ? 'bg-warning/10 text-warning'
        : 'bg-neutral-100 text-neutral-600'

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      {label}
    </span>
  )
}

function ActivityTab({ events }: { events: CaseEvent[] }) {
  if (events.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-6 text-sm text-neutral-600">
        No activity has been recorded for this case yet. Document uploads, case updates, and review actions will appear here.
      </div>
    )
  }
  return (
    <div className="space-y-4">
      <TrackerWorkspacePanel
        eyebrow="Activity workspace"
        title="Case timeline"
        helpText="Review the case history here to see what changed, when it changed, and whether follow up may still be needed."
        summaryTiles={[
          {
            label: 'Events recorded',
            value: `${events.length}`,
            icon: <Clock size={18} aria-hidden="true" />,
          },
          {
            label: 'Latest activity',
            value: formatDateTime(events[0]?.occurred_at ?? ''),
            icon: <CheckCircle2 size={18} aria-hidden="true" />,
          },
        ]}
      />
      <ul className="bg-white border border-neutral-200 rounded-lg shadow-sm divide-y divide-neutral-200">
        {events.map((e) => {
          const presentation = presentCaseEvent(e)
          const Icon = presentation.icon
          return (
            <li key={e.id} className="flex items-start gap-3 p-4">
              <div className={`p-2 rounded flex-shrink-0 ${toneClasses(presentation.tone)}`}>
                <Icon size={16} aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-sm font-semibold text-primary">{presentation.title}</div>
                  <ActivityChip label={presentation.category} tone={presentation.tone} />
                </div>
                <div className="mt-1 text-sm text-neutral-700">{presentation.description}</div>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-neutral-500">
                  <span>{formatDateTime(e.occurred_at)}</span>
                  {presentation.actorLabel && <span>By {presentation.actorLabel}</span>}
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function ActivityChip({ label, tone }: { label: string; tone: ActivityTone }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${chipToneClasses(tone)}`}>
      {label}
    </span>
  )
}

function toneClasses(tone: ActivityTone) {
  switch (tone) {
    case 'success':
      return 'bg-success/10 text-success'
    case 'warning':
      return 'bg-warning/10 text-warning'
    case 'info':
      return 'bg-secondary-lightest text-secondary'
    default:
      return 'bg-neutral-100 text-neutral-600'
  }
}

function chipToneClasses(tone: ActivityTone) {
  switch (tone) {
    case 'success':
      return 'bg-success/10 text-success'
    case 'warning':
      return 'bg-warning/10 text-warning'
    case 'info':
      return 'bg-secondary-lightest text-secondary'
    default:
      return 'bg-neutral-100 text-neutral-600'
  }
}

// keep Clock import live for tree-shaking sanity
void Clock
